import React, { useState } from "react";
import {
    BarChart3,
    Workflow,
    Users,
    Receipt,
    FileSignature,
    Briefcase,
    Store,
    Building2,
    Stethoscope,
    Factory,
    HandshakeIcon,
    ArrowRight,
    Check,
    ShoppingCart,
} from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { FaqSection } from "../components/FaqSection";
import { HeroCarousel } from "../components/HeroCarousel";
import { InteractiveTour } from "../components/InteractiveTour";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { SecurityBadges } from "../components/SecurityBadges";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { VideoModal, VideoTriggerButton } from "../components/VideoModal";

const PRODUCTS = [
    {
        key: "mativex",
        name: "Mativex",
        sub: "Analytics & Sales Intelligence",
        desc: "Real-time decisions from sales, inventory, and collections.",
        icon: BarChart3,
        forWho: "for COOs · CFOs",
    },
    {
        key: "vexen",
        name: "Vexen",
        sub: "Operations Intelligence",
        desc: "Project-level visibility for complex operational businesses.",
        icon: Workflow,
        forWho: "for project & plant heads",
    },
    {
        key: "karya",
        name: "Karya",
        sub: "HR & People Operations",
        desc: "Attendance, payroll, leaves, and onboarding in one place.",
        icon: Users,
        forWho: "for HR heads",
    },
    {
        key: "vyaahar",
        name: "Vyaahar",
        sub: "Finance & Bookkeeping",
        desc: "Tax-compliant invoicing, AR/AP, and financial reporting.",
        icon: Receipt,
        forWho: "for finance teams",
    },
    {
        key: "attestra",
        name: "Attestra",
        sub: "Digital Signing",
        desc: "Contracts, approvals, and agreements signed in minutes.",
        icon: FileSignature,
        forWho: "for ops · sales · legal",
    },
    {
        key: "acrium",
        name: "Acrium",
        sub: "Project Cost & Procurement",
        desc: "Boardroom-grade control over cost, procurement, and vendors.",
        icon: Briefcase,
        forWho: "for real estate · infra · EPC",
    },
    {
        key: "kretara",
        name: "Kretara",
        sub: "Procurement Intelligence",
        desc: "Multi-step approvals, vendor scorecards, and live category spend.",
        icon: ShoppingCart,
        forWho: "for retail · category teams",
    },
];

const PERSONAS = [
    { key: "retail", label: "Retail" },
    { key: "real-estate", label: "Real Estate" },
    { key: "manufacturing", label: "Manufacturing" },
    { key: "services", label: "Services" },
    { key: "healthcare", label: "Healthcare" },
];

const INDUSTRIES = [
    {
        key: "retail",
        name: "Retail",
        icon: Store,
        uses: [
            "Sales dashboard across stores",
            "Inventory ageing & turnover",
            "Customer feedback signals",
        ],
    },
    {
        key: "real-estate",
        name: "Real Estate",
        icon: Building2,
        uses: [
            "Bookings & collections tracker",
            "Inventory absorption analytics",
            "Project cost control",
        ],
    },
    {
        key: "healthcare",
        name: "Healthcare",
        icon: Stethoscope,
        uses: [
            "Revenue cycle visibility",
            "Denial & rejection tracking",
            "Provider productivity",
        ],
    },
    {
        key: "manufacturing",
        name: "Manufacturing",
        icon: Factory,
        uses: [
            "Production tracking",
            "Procurement intelligence",
            "Cost variance reporting",
        ],
    },
    {
        key: "services",
        name: "Services",
        icon: HandshakeIcon,
        uses: [
            "Sales pipeline & conversion",
            "Utilization & billable hours",
            "Receivables tracking",
        ],
    },
];

export const HomePage = () => {
    const [tourOpen, setTourOpen] = useState(false);
    return (
        <>
            {/* HERO */}
            <section className="hero" id="home" data-testid="hero-section">
                <div className="container">
                    <div className="hero-grid">
                        <FadeUpGroup>
                            <div className="fade-up in">
                                <span
                                    className="eyebrow hero-eyebrow"
                                    data-testid="hero-eyebrow"
                                >
                                    Scitus.ai · One platform. Standalone products.
                                </span>
                                <h1 className="h1" data-testid="hero-h1">
                                    Stop buying suites you don't use. Pick the products your business actually needs.
                                </h1>
                                <p
                                    className="lead"
                                    style={{ marginTop: 22, maxWidth: 580 }}
                                    data-testid="hero-subtitle"
                                >
                                    Operator-grade products for analytics, ops,
                                    HR, finance, signing, project cost.
                                    Transparent per-product pricing. Bundle
                                    two or more and save 25% — automatically.
                                </p>
                                <div className="hero-cta-row">
                                    <a
                                        href="/contact"
                                        className="btn btn-primary btn-lg"
                                        data-testid="hero-book-demo-btn"
                                    >
                                        Book a Demo
                                        <ArrowRight size={18} />
                                    </a>
                                    <a
                                        href="/pricing"
                                        className="btn btn-ghost btn-lg"
                                        data-testid="hero-pricing-btn"
                                    >
                                        See pricing
                                    </a>
                                    <a
                                        href="/products-strip"
                                        className="link-arrow hero-tertiary-cta"
                                        data-testid="hero-explore-btn"
                                    >
                                        Explore products <ArrowRight size={14} />
                                    </a>
                                </div>
                                <div className="hero-trust">
                                    <span data-testid="hero-trust-price">
                                        <span className="dot" />
                                        From <strong>₹160 / employee / mo</strong>
                                    </span>
                                    <span>
                                        <span className="dot" />
                                        Per-product pricing
                                    </span>
                                    <span>
                                        <span className="dot" />
                                        Bundle + Annual = up to 40% off
                                    </span>
                                </div>
                                <p className="hero-customer-proof" data-testid="hero-customer-proof">
                                    Built for operators across{" "}
                                    <strong>real estate, retail, healthcare,
                                    manufacturing, and services</strong> — in India
                                    and the US.
                                </p>

                                {/* In-production strip — Mativex & Vexen are live with paying operators (under prior brand) */}
                                <div
                                    className="transparency-strip"
                                    data-testid="transparency-strip"
                                >
                                    <span className="ts-pill ts-pill-live">
                                        <span className="ts-dot ts-dot-live" />
                                        In production
                                    </span>
                                    <span className="ts-text">
                                        Already running for paying operators across real estate and retail. <a href="/contact" data-testid="ts-customer-one">
                                            Want yours to be next? →
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </FadeUpGroup>
                        <HeroCarousel />
                    </div>
                </div>
            </section>

            {/* PERSONA ROUTER — "What are you running?" — quick filter to industry pages */}
            <section
                className="section persona-router-section"
                data-testid="persona-router"
                style={{ paddingTop: 36, paddingBottom: 36 }}
            >
                <div className="container">
                    <div className="persona-router">
                        <span className="persona-router-label">
                            What are you running?
                        </span>
                        <div className="persona-router-chips">
                            {PERSONAS.map((p) => (
                                <a
                                    key={p.key}
                                    href={`/industries/${p.key}`}
                                    className="persona-chip"
                                    data-testid={`persona-chip-${p.key}`}
                                >
                                    {p.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PRODUCT STRIP */}
            <section
                className="section"
                id="products-strip"
                data-testid="products-strip"
            >
                <div className="container">
                    <div className="section-head">
                        <span className="eyebrow">Pick what you need.</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>
                            Standalone products. Each one wins on its own.
                        </h2>
                        <p>
                            Buy any one. Combine two or more and save 25% — every Scitus product shares the same identity, data, and billing relationship.
                        </p>
                    </div>
                    <FadeUpGroup>
                        <div className="product-strip">
                            {PRODUCTS.map((p) => (
                                <a
                                    key={p.key}
                                    href={`/products/${p.key}`}
                                    className="product-card fade-up"
                                    data-testid={`product-card-${p.key}`}
                                >
                                    <span className="icon-pill">
                                        <p.icon size={20} />
                                    </span>
                                    <span className="for-who" data-testid={`product-forwho-${p.key}`}>
                                        {p.forWho}
                                    </span>
                                    <span className="name">{p.name}</span>
                                    <span className="sub">{p.sub}</span>
                                    <p className="desc">{p.desc}</p>
                                    <span className="link-arrow">
                                        Learn more <ArrowRight size={16} />
                                    </span>
                                </a>
                            ))}
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section
                className="section"
                id="industries-home"
                data-testid="industries-home"
                style={{ background: "var(--surface)" }}
            >
                <div className="container">
                    <div className="section-head">
                        <span className="eyebrow">Industry Solutions</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>
                            Built for every industry
                        </h2>
                        <p>
                            Pre-built playbooks and dashboards calibrated for
                            the businesses we serve every day.
                        </p>
                    </div>
                    <FadeUpGroup>
                        <div className="industry-grid">
                            {INDUSTRIES.map((ind) => (
                                <div
                                    key={ind.key}
                                    className="industry-card fade-up"
                                    data-testid={`industry-card-${ind.key}`}
                                >
                                    <div className="head-row">
                                        <span className="icon-square">
                                            <ind.icon size={22} />
                                        </span>
                                        <h3 className="h3">{ind.name}</h3>
                                    </div>
                                    <ul>
                                        {ind.uses.map((u) => (
                                            <li key={u}>{u}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </FadeUpGroup>
                    <div className="center mt-12">
                        <a
                            href="/industries"
                            className="link-arrow"
                            data-testid="see-all-solutions-link"
                        >
                            See all solutions <ArrowRight size={16} />
                        </a>
                    </div>
                </div>
            </section>

            {/* PLATFORM STORY */}
            <section className="section" id="platform" data-testid="platform-section">
                <div className="container">
                    <div className="section-head">
                        <span className="eyebrow">The Platform</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>
                            Standalone products. One platform underneath.
                        </h2>
                        <p>
                            Each Scitus.ai product is best-in-class on its own. The moment you take a second, they share identity, data, and a single billing relationship — without any of the lock-in that big "all-in-one" suites force on you.
                        </p>
                    </div>
                    <FadeUpGroup>
                        <div className="steps-grid">
                            <div className="step-card fade-up">
                                <div className="step-num">Step 01</div>
                                <h3>Pick what you need</h3>
                                <p>
                                    Start with one product. Mativex, Vexen, Karya — whichever solves your loudest problem first.
                                </p>
                            </div>
                            <div className="step-card fade-up">
                                <div className="step-num">Step 02</div>
                                <h3>Add when you're ready</h3>
                                <p>
                                    Every additional product joins the same identity and data layer automatically — and the bundle discount kicks in at two.
                                </p>
                            </div>
                            <div className="step-card fade-up">
                                <div className="step-num">Step 03</div>
                                <h3>Operate from one place</h3>
                                <p>
                                    Approve, sign, pay, and decide from a unified workspace — with a single audit trail across products.
                                </p>
                            </div>
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* WHY SCITUS — stats */}
            <section
                className="section"
                style={{ background: "var(--surface)" }}
                data-testid="why-scitus-section"
            >
                <div className="container">
                    <div className="section-head">
                        <span className="eyebrow">Why Scitus</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>
                            A platform that thinks like an operator
                        </h2>
                    </div>
                    <FadeUpGroup>
                        <div className="stat-grid">
                            <div
                                className="stat-card fade-up"
                                data-testid="stat-products"
                            >
                                <div className="num">One</div>
                                <div className="label">
                                    Platform — identity, data, billing shared across every product
                                </div>
                            </div>
                            <div
                                className="stat-card fade-up"
                                data-testid="stat-industry"
                            >
                                <div className="num">Any</div>
                                <div className="label">
                                    Industry — retail, real estate, healthcare,
                                    manufacturing, services
                                </div>
                            </div>
                            <div
                                className="stat-card fade-up"
                                data-testid="stat-geo"
                            >
                                <div className="num"><AnimatedCounter value="Global" /></div>
                                <div className="label">
                                    Markets — designed for global teams
                                </div>
                            </div>
                            <div
                                className="stat-card fade-up"
                                data-testid="stat-emergent"
                            >
                                <div className="num">AI-first</div>
                                <div className="label">
                                    Emergent-built copilots in every product
                                </div>
                            </div>
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            <InteractiveTour />
            <SecurityBadges />

            {/* DEMO CTA BANNER — placed after the proof (Tour + Security) per F7 */}
            <section
                className="section"
                style={{ paddingTop: 0 }}
                data-testid="cta-banner-section"
            >
                <div className="container">
                    <div className="cta-banner">
                        <div>
                            <h2 className="h2">
                                See Scitus.ai in action.
                            </h2>
                            <p className="lead mt-4">
                                Book a 30-minute demo. We&apos;ll walk through
                                the products relevant to your business and share
                                a tailored rollout plan.
                            </p>
                        </div>
                        <a
                            href="/contact"
                            className="btn btn-primary btn-lg"
                            data-testid="cta-banner-schedule-btn"
                        >
                            Schedule Demo <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </section>

            {/* "Why Scitus.ai" — premium card-based section that matches
                the rest of the site's dark aesthetic. Replaces the earlier
                plain-text recap; product details already live in the grid
                above so this section focuses purely on the platform value
                proposition. */}
            <section className="section" data-testid="home-why-scitus" aria-labelledby="home-why-h2">
                <div className="container">
                    <div className="section-head" style={{ textAlign: "center" }}>
                        <span className="eyebrow">Why Scitus.ai</span>
                        <h2 id="home-why-h2" className="h2" style={{ marginTop: 16 }}>
                            Seven sharp products. One quiet platform.
                        </h2>
                        <p style={{ maxWidth: 640, margin: "16px auto 0" }}>
                            Standalone where it matters. Integrated where it counts.
                        </p>
                    </div>

                    <div
                        className="why-scitus-grid"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                            gap: 18,
                            marginTop: 48,
                            maxWidth: 1100,
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                        data-testid="why-scitus-grid"
                    >
                        {[
                            {
                                h: "One login. One bill. One audit trail.",
                                p: "Seven complete products that share identity, data, and billing — so the second product you adopt feels like another tab, not another vendor evaluation.",
                            },
                            {
                                h: "Live in days, not quarters.",
                                p: "No 18-month ERP rollout. No army of implementation consultants. Most teams are running their first Scitus product on real transactions within a fortnight.",
                            },
                            {
                                h: "AI copilot on every product.",
                                p: "Every product ships with an AI copilot grounded in your own data — answering questions in plain language, drafting approvals, flagging anomalies. Not an upsell. Not a separate SKU. Included.",
                            },
                            {
                                h: "Sits on top of your existing ERP.",
                                p: "Already on Tally, SAP, Oracle, or a custom ERP? Keep it. Scitus reads from it and gives your operators the intelligence layer that your ERP was never built to provide.",
                            },
                            {
                                h: "Human support, included.",
                                p: "Every plan ships with humans who actually use the product. No \"premium support tier\", no chatbot mazes. When something breaks, you talk to a builder.",
                            },
                            {
                                h: "Built for operators, by operators.",
                                p: "Every product manager on the Scitus team has run a real business or a real function. Code is downstream of context — and the product shows it.",
                            },
                        ].map((item) => (
                            <div
                                key={item.h}
                                className="card"
                                style={{ padding: "24px 26px" }}
                                data-testid={`why-card-${item.h.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                            >
                                <h3
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 700,
                                        color: "var(--text)",
                                        margin: 0,
                                        fontSize: "1.05rem",
                                        lineHeight: 1.35,
                                    }}
                                >
                                    {item.h}
                                </h3>
                                <p
                                    className="muted"
                                    style={{ margin: "10px 0 0 0", fontSize: "0.92rem", lineHeight: 1.65 }}
                                >
                                    {item.p}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <FaqSection
                heading="Operator questions, answered."
                subheading="The four questions we get asked on every demo call."
                testIdPrefix="home-faq"
                items={[
                    {
                        q: "Do I need to rip out my existing ERP to use Scitus?",
                        a: "No — this is by design. Scitus sits on top of your existing ERP (Tally, SAP, Oracle, or custom). We do not replace your ERP. We replace the spreadsheets your team builds around it. Your ERP records transactions. Scitus tells you what they mean.",
                    },
                    {
                        q: "How long does implementation actually take?",
                        a: "Most teams are running their first Scitus product on real transactions within 7–14 days. Full suite rollouts (Retail Suite, Real Estate Suite) take 30–60 days end-to-end including data migration, connector builds, training, and hypercare. No 18-month ERP rollout. No army of implementation consultants.",
                    },
                    {
                        q: "Can I start with one product and add more later?",
                        a: "Yes. Pick any single product at the listed price. When you add a second product, the 25% bundle discount applies automatically to everything on your account from that point forward. Add and drop products anytime — charges pro-rate to the day.",
                    },
                    {
                        q: "Where is our data hosted and who can see it?",
                        a: "Customer data lives in your own logical workspace, hosted on AWS Mumbai by default (India residency). Enterprise customers can opt into Singapore, Frankfurt, or US-East regions. Access is role-based at screen, action, and row level. We have a published DPA at /dpa and never train models on customer data.",
                    },
                ]}
            />

            <NewsletterSignup />

            <VideoModal open={tourOpen} onClose={() => setTourOpen(false)} />
        </>
    );
};

export default HomePage;
